import React, { useState, useMemo } from 'react';
import { DevelopmentScenario } from '@/services/fsiReport';
import { Check, Home, Building2, ShoppingCart, AlertCircle } from 'lucide-react';

interface DevelopmentScenarioSelectorProps {
  scenarios: DevelopmentScenario[];
  zone: string;
  plotAreaSqft: number;
  selected: string;
  onChange: (value: string) => void;
}

const DevelopmentScenarioSelector: React.FC<DevelopmentScenarioSelectorProps> = ({
  scenarios,
  zone,
  plotAreaSqft,
  selected,
  onChange,
}) => {
  const [category, setCategory] = useState<'residential' | 'commercial'>('residential');

  const filteredScenarios = useMemo(() => {
    return scenarios.filter(
      (s) =>
        s.category === category &&
        s.suitable_for.includes(zone) &&
        plotAreaSqft >= s.min_area_sqft
    );
  }, [scenarios, category, zone, plotAreaSqft]);

  const getCategoryIcon = (cat: string) => {
    return cat === 'residential' ? <Home className="w-5 h-5" /> : <Building2 className="w-5 h-5" />;
  };

  return (
    <div className="mb-8">
      <h3 className="text-xl font-bold text-gray-800 mb-2">Step 2: Select Development Type</h3>
      <p className="text-gray-600 mb-4">
        Choose the type of development you want to build on this plot
      </p>

      {/* Category Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
            category === 'residential'
              ? 'bg-green-600 text-white shadow-md'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setCategory('residential')}
        >
          <Home className="w-5 h-5" />
          Residential
        </button>
        <button
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
            category === 'commercial'
              ? 'bg-green-600 text-white shadow-md'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setCategory('commercial')}
        >
          <Building2 className="w-5 h-5" />
          Commercial
        </button>
      </div>

      {/* Scenario Grid */}
      {filteredScenarios.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredScenarios.map((scenario) => (
            <div
              key={scenario.id}
              className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selected === scenario.id
                  ? 'border-green-600 bg-green-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-green-300 hover:shadow'
              }`}
              onClick={() => onChange(scenario.id)}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`p-2 rounded-lg ${
                  category === 'residential' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                }`}>
                  {getCategoryIcon(category)}
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-800">{scenario.name}</h4>
                  {scenario.max_floors && (
                    <p className="text-xs text-gray-500">Max {scenario.max_floors} floors</p>
                  )}
                </div>
                {selected === scenario.id && (
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mb-3">{scenario.description}</p>
              
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Cost per sqft:</span>
                  <span className="font-bold text-gray-800">₹{scenario.cost_per_sqft.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Min area:</span>
                  <span className="font-medium text-gray-700">{scenario.min_area_sqft.toLocaleString()} sqft</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
          <h4 className="font-bold text-gray-800 mb-2">No Scenarios Available</h4>
          <p className="text-gray-600 text-sm">
            No {category} development scenarios are suitable for this plot's zone ({zone}) and area ({plotAreaSqft ? plotAreaSqft.toFixed(0) : 'N/A'} sqft).
          </p>
        </div>
      )}
    </div>
  );
};

export default DevelopmentScenarioSelector;
